<template>
  <div class="carousel-container">
    <!-- Botón Anterior -->
    <button 
      v-if="navigation && canNavigate"
      class="carousel-nav-button prev" 
      @click="prevSlide"
      aria-label="Anterior"
      :disabled="!wrapAround && currentIndex === 0"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>

    <!-- Viewport del Carrusel -->
    <div class="carousel-viewport" ref="carouselViewport">
      <div 
        class="carousel-slides"
        ref="carouselSlides"
        :style="{ transform: `translateX(-${currentPosition}%)`, cursor: isDragging ? 'grabbing' : 'grab' }"
        @touchstart.passive="handleTouchStart" 
        @touchmove.passive="handleTouchMove" 
        @touchend.passive="handleTouchEnd"
        @mousedown.prevent="handleMouseDown" 
        @mousemove.prevent="handleMouseMove" 
        @mouseup.prevent="handleMouseUp"
        @mouseleave.prevent="handleMouseLeave"
        @mouseenter="pauseAutoplayOnInteraction" 
        @mouseleave="resumeAutoplayAfterInteraction"
      >
        <slot></slot>
      </div>
      
      <!-- Indicadores de paginación -->
      <div v-if="pagination && canNavigate" class="carousel-pagination">
        <button 
          v-for="(_, index) in pageCount" 
          :key="index"
          :class="{ active: index === currentPage }"
          @click="goToPage(index)"
          :aria-label="`Ir a la página ${index + 1}`"
          :disabled="index === currentPage"
        ></button>
      </div>
    </div>

    <!-- Botón Siguiente -->
    <button 
      v-if="navigation && canNavigate"
      class="carousel-nav-button next" 
      @click="nextSlide"
      aria-label="Siguiente"
      :disabled="!wrapAround && currentIndex === maxIndex"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 6L15 12L9 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, defineProps, defineExpose } from 'vue';

const props = defineProps({
  autoplay: {
    type: Number,
    default: 0 // 0 = sin autoplay, otro valor = ms entre cambios
  },
  navigation: {
    type: Boolean,
    default: true
  },
  pagination: {
    type: Boolean,
    default: true
  },
  wrapAround: {
    type: Boolean,
    default: true
  },
  pauseOnHover: {
    type: Boolean,
    default: true
  },
  itemsToShow: {
    type: Number,
    default: 1
  },
  swipeThreshold: {
    type: Number,
    default: 25 // Píxeles a mover para considerar swipe
  }
});

const carouselViewport = ref(null); // Ref para el nuevo viewport
const carouselSlides = ref(null);
const currentIndex = ref(0); // Índice del primer slide visible
const slidesCount = ref(0);
const autoplayInterval = ref(null);
const isPausedByInteraction = ref(false); // Flag para pausa por hover o drag

// --- Estado para Swipe/Drag ---
const isDragging = ref(false);
const startX = ref(0);
const currentX = ref(0);
const dragStartTime = ref(0);
// --- Fin Estado Swipe/Drag ---

const canNavigate = computed(() => slidesCount.value > props.itemsToShow);
const maxIndex = computed(() => Math.max(0, slidesCount.value - props.itemsToShow));

// --- Lógica de paginación --- 
// Asume que cada página muestra 'itemsToShow' slides
const pageCount = computed(() => {
  if (!canNavigate.value) return 0;
  return Math.ceil(slidesCount.value / props.itemsToShow);
});

const currentPage = computed(() => {
  if (!canNavigate.value) return 0;
  return Math.max(0, Math.floor(currentIndex.value / props.itemsToShow));
});

const goToPage = (pageIndex) => {
  currentIndex.value = Math.min(pageIndex * props.itemsToShow, maxIndex.value);
  resetAutoplayTimer();
};
// --- Fin lógica de paginación ---

const currentPosition = computed(() => {
  const slideWidthPercentage = 100 / props.itemsToShow;
  return currentIndex.value * slideWidthPercentage;
});

// --- Lógica Swipe/Drag ---
const handleDragStart = (clientX) => {
  if (!canNavigate.value) return;
  isDragging.value = true;
  startX.value = clientX;
  currentX.value = clientX;
  dragStartTime.value = Date.now();
  // Pausar autoplay al iniciar drag si está configurado
  if (props.pauseOnHover) {
      stopAutoplay(); // Pausa inmediata al empezar a arrastrar
      isPausedByInteraction.value = true; // Marca como pausado por el usuario
  }
  // Cambiar cursor
  if (carouselSlides.value) carouselSlides.value.style.cursor = 'grabbing';
};

const handleDragMove = (clientX) => {
  if (!isDragging.value || !canNavigate.value) return;
  currentX.value = clientX;
  // Opcional: Podrías añadir un efecto visual de arrastre aquí, 
  // modificando el transform, pero es más complejo.
};

const handleDragEnd = () => {
  if (!isDragging.value || !canNavigate.value) return;
  isDragging.value = false;
  const deltaX = currentX.value - startX.value;
  const dragEndTime = Date.now();
  const dragDuration = dragEndTime - dragStartTime.value;

  // Reset cursor
  if (carouselSlides.value) carouselSlides.value.style.cursor = 'grab';

  // Determinar si es un swipe válido
  // Condición: Mover más que el threshold Y rápido (ej. < 500ms)
  if (Math.abs(deltaX) > props.swipeThreshold && dragDuration < 500) { 
    if (deltaX < 0) {
      // Swipe Izquierda (Next)
      nextSlide();
    } else {
      // Swipe Derecha (Prev)
      prevSlide();
    }
  } 
  // Si no fue un swipe válido, o después de un swipe,
  // reanudar autoplay si estaba pausado por esta interacción
  resumeAutoplayAfterInteraction(); 
};

// Handlers específicos para Touch
const handleTouchStart = (event) => {
  // Usar el primer punto de contacto
  handleDragStart(event.touches[0].clientX);
};
const handleTouchMove = (event) => {
  handleDragMove(event.touches[0].clientX);
};
const handleTouchEnd = () => {
  handleDragEnd();
};

// Handlers específicos para Mouse
const handleMouseDown = (event) => {
  handleDragStart(event.clientX);
};
const handleMouseMove = (event) => {
  handleDragMove(event.clientX);
};
const handleMouseUp = () => {
  handleDragEnd();
};
const handleMouseLeave = () => {
  // Si el mouse sale mientras se arrastra, termina el drag
  if (isDragging.value) {
    handleDragEnd();
  }
};
// --- Fin Lógica Swipe/Drag ---

// --- Autoplay y Pausa por Interacción ---
const pauseAutoplayOnInteraction = () => {
  // Pausa si está configurado y no está ya pausado por interacción
  if (props.pauseOnHover && !isPausedByInteraction.value && autoplayInterval.value) {
    stopAutoplay();
    isPausedByInteraction.value = true;
  }
};

const resumeAutoplayAfterInteraction = () => {
  // Reanuda solo si fue pausado por interacción (hover o drag) y no se está arrastrando
  if (props.pauseOnHover && isPausedByInteraction.value && !isDragging.value) {
    isPausedByInteraction.value = false;
    resetAutoplayTimer(); // Reinicia el timer para intervalo completo
  }
};

const startAutoplay = () => {
  // Solo inicia si hay autoplay, se puede navegar y NO está pausado por interacción
  if (props.autoplay > 0 && canNavigate.value && !autoplayInterval.value && !isPausedByInteraction.value) {
    autoplayInterval.value = setInterval(() => {
       nextSlide(); 
    }, props.autoplay);
  }
};

const stopAutoplay = () => {
  if (autoplayInterval.value) {
    clearInterval(autoplayInterval.value);
    autoplayInterval.value = null;
  }
};

const resetAutoplayTimer = () => {
  stopAutoplay();
  startAutoplay(); // startAutoplay ya verifica si debe iniciarse
};

// Inicializar el carrusel
onMounted(() => {
  updateSlidesCount();
  startAutoplay(); 

  const observer = new MutationObserver(() => {
    updateSlidesCount();
  });
  
  if (carouselSlides.value) {
    observer.observe(carouselSlides.value, { childList: true });
  }
  
  // Limpieza de listeners al desmontar se maneja en onBeforeUnmount
});

onBeforeUnmount(() => {
  stopAutoplay();
  // Aquí podrías remover explícitamente los listeners del mouse/touch 
  // si hubiera riesgo de memory leaks, aunque Vue suele manejarlos bien.
});

const updateSlidesCount = () => {
  if (carouselSlides.value) {
    const oldSlidesCount = slidesCount.value;
    slidesCount.value = carouselSlides.value.children.length;
    if (slidesCount.value !== oldSlidesCount) {
       currentIndex.value = Math.min(currentIndex.value, maxIndex.value);
       // Reiniciar autoplay si cambia el número de slides y ahora se puede/no se puede navegar
       resetAutoplayTimer(); 
    }
  }
};

const nextSlide = () => {
  if (!canNavigate.value) return;
  if (currentIndex.value < maxIndex.value) {
    currentIndex.value++;
  } else if (props.wrapAround) {
    currentIndex.value = 0;
  }
  // No reiniciar timer aquí si es llamado desde autoplay
  if(!autoplayInterval.value) resetAutoplayTimer(); 
};

const prevSlide = () => {
  if (!canNavigate.value) return;
  if (currentIndex.value > 0) {
    currentIndex.value--;
  } else if (props.wrapAround) {
    currentIndex.value = maxIndex.value;
  }
  if(!autoplayInterval.value) resetAutoplayTimer();
};

// goToSlide sigue funcionando con el índice del slide, no de la página
const goToSlide = (index) => {
  if (!canNavigate.value) return;
  const validIndex = Math.max(0, Math.min(index, slidesCount.value - 1));
  currentIndex.value = Math.min(validIndex, maxIndex.value);
  resetAutoplayTimer();
};

// Observadores
watch([() => props.autoplay, () => props.itemsToShow, slidesCount], () => {
  // Lógica movida a updateSlidesCount y resetAutoplayTimer
  resetAutoplayTimer();
});

watch(canNavigate, () => {
  // Lógica movida a updateSlidesCount y resetAutoplayTimer
  resetAutoplayTimer(); 
});

// Exponer métodos
defineExpose({
  nextSlide,
  prevSlide,
  goToSlide,
  goToPage // Exponer goToPage también
});
</script>

<style scoped>
.carousel-container {
  display: flex; /* Usa Flexbox */
  align-items: center; /* Centra verticalmente botones y viewport */
  position: relative; /* Para posicionar la paginación */
  width: 100%;
  /* Prevenir selección de texto durante drag */
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.carousel-viewport {
  flex-grow: 1; /* El viewport ocupa el espacio restante */
  overflow: hidden; /* Oculta los slides que no caben */
  position: relative; /* Necesario para la paginación absoluta */
  /* Podrías añadir un margin horizontal si quieres más espacio entre botones y viewport */
  /* margin: 0 10px; */ 
}

.carousel-slides {
  display: flex;
  transition: transform 0.5s ease;
  --slide-width: calc(100% / v-bind('props.itemsToShow'));
  /* cursor: grab; */ /* Se maneja dinámicamente en el style binding */
  /* Asegurar que las imágenes dentro no sean arrastrables por defecto */
}
.carousel-slides ::v-deep(img) { 
  pointer-events: none;
} 

.carousel-nav-button {
  background-color: rgba(255, 255, 255, 0.92);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(184, 177, 189, 0.4);
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #734A91;
  box-shadow: 0 6px 18px rgba(115, 74, 145, 0.12);
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1),
              background-color 0.35s ease,
              color 0.35s ease,
              box-shadow 0.35s ease;
  flex-shrink: 0;
  z-index: 10;
}

.carousel-nav-button :deep(svg),
.carousel-nav-button :deep(img) {
  width: 18px;
  height: 18px;
  transition: transform 0.35s ease;
}

.carousel-nav-button:hover:not(:disabled) {
  background-color: #734A91;
  color: #ffffff;
  box-shadow: 0 12px 30px rgba(115, 74, 145, 0.32);
  transform: scale(1.06);
}

.carousel-nav-button:hover:not(:disabled) :deep(svg) {
  transform: translateX(1px);
}

.carousel-nav-button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.carousel-nav-button.prev {
  margin-right: 12px;
}

.carousel-nav-button.next {
  margin-left: 12px;
}

.carousel-pagination {
  position: absolute;
  bottom: 14px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  z-index: 10;
  padding: 6px 10px;
  background: rgba(0, 0, 0, 0.18);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  border-radius: 9999px;
}

.carousel-pagination button {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background-color: rgba(255, 255, 255, 0.55);
  border: none;
  cursor: pointer;
  transition: width 0.35s cubic-bezier(0.22, 1, 0.36, 1),
              background-color 0.35s ease,
              transform 0.35s ease;
  padding: 0;
}

.carousel-pagination button:hover:not(.active) {
  background-color: rgba(255, 255, 255, 0.85);
  transform: scale(1.15);
}

.carousel-pagination button.active {
  background-color: #ffffff;
  width: 24px;
  border-color: transparent;
}

.carousel-pagination button:disabled {
  cursor: default;
}

@media (max-width: 768px) {
  .carousel-nav-button {
    width: 40px;
    height: 40px;
  }
  .carousel-nav-button.prev {
    margin-right: 6px;
  }
  .carousel-nav-button.next {
    margin-left: 6px;
  }
  .carousel-pagination {
    bottom: 8px;
    padding: 4px 8px;
  }
  .carousel-pagination button {
    width: 7px;
    height: 7px;
  }
  .carousel-pagination button.active {
    width: 18px;
  }
}
</style> 